/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import MultiSelect from 'react-multi-select-component';
import ModalBody from 'reactstrap/lib/ModalBody';
import * as React from 'react';
import { IModuleProps, Modal, ModalHeader } from '@msdyn365-commerce-modules/utilities';

import { IReportsExportData } from './reports-export.data';
import { IReportsExportProps } from './reports-export.props.autogenerated';
import { getPriceByManufacturerAsync, getSalesReportAsync } from '../../actions/DataActionExtension.g';
import { IManufacturerCodeResponseEntity } from '../../actions/DataServiceEntities.g';

import { RenderCard } from './components/render-card';

export interface IReportsExportViewProps extends IReportsExportProps<IReportsExportData> {
    moduleProps: IModuleProps;
    heading?: React.ReactNode;
    reportsList: React.ReactElement;
    modal: React.ReactNode;
}

export interface IReportsExportState {
    priceByMfrSelectedMfrMultiSelect: Option[];
    priceByMfrSelectedMfrCodes: string[];
    salesByMfrSelectedMfrMultiSelect: Option[];
    salesByMfrSelectedMfrCodes: string[];
    salesByPartSelectedPartMultiSelect: Option[];
    salesByPartSelectedPartCodes: string[];
    salesByPartSelectedSearchType: string;
    singleManufacturerPriceType: string;
    isModalActive: boolean;
    modalMessage: string;
    isModalMessageSuccess: boolean;
}

export interface Option {
    label: string;
    value: string;
    key?: string;
    disabled?: boolean;
}

/**
 *
 * ReportsExport component
 * @extends {React.PureComponent<IReportsExportProps<IReportsExportData>>}
 */
class ReportsExport extends React.PureComponent<IReportsExportProps<IReportsExportData>, IReportsExportState> {
    constructor(props: IReportsExportProps<IReportsExportData>) {
        super(props);

        this._setManufacturerCodeState = this._setManufacturerCodeState.bind(this);
        this._setSalesByPartSearchTypeState = this._setSalesByPartSearchTypeState.bind(this);
        this._setSingleManufacturerPriceTypeState = this._setSingleManufacturerPriceTypeState.bind(this);
        this._handleButtonClick = this._handleButtonClick.bind(this);

        this.state = {
            priceByMfrSelectedMfrMultiSelect: [],
            priceByMfrSelectedMfrCodes: [],
            salesByMfrSelectedMfrMultiSelect: [],
            salesByMfrSelectedMfrCodes: [],
            salesByPartSelectedPartMultiSelect: [],
            salesByPartSelectedPartCodes: [],
            salesByPartSelectedSearchType: '',
            singleManufacturerPriceType: '',
            isModalActive: false,
            modalMessage: '',
            isModalMessageSuccess: false
        };
    }

    /**
     * Gets manufacturer codes from data
     * @private
     */
    private _getManufacturerCodeList = (): IManufacturerCodeResponseEntity[] => {
        const codesResults = this.props.data.manufacturerCodes.result ? this.props.data.manufacturerCodes.result : [];
        // @ts-ignore
        return codesResults.manufacturerCodes;
    };

    private _getPartList = () => {
        const partList = [
            { PartNo: 'Organization', Description: 'Org' },
            { PartNo: 'Location', Description: 'Loc' }
        ];
        // { PartNo: 'ITW00786', Description: '.300x2-1/2 Dr Pin 1516 100 pk' },
        // { PartNo: 'ITW00803', Description: '.300x1-1/2 Pin w/washer 100 p' }
        return partList;
    };

    private _setSalesByPartSearchTypeState(e: React.ChangeEvent<HTMLSelectElement>) {
        const target = e.target as HTMLSelectElement;
        this.setState({ salesByPartSelectedSearchType: target.value });
    }

    private _setSingleManufacturerPriceTypeState(e: React.ChangeEvent<HTMLSelectElement>) {
        const target = e.target as HTMLSelectElement;
        this.setState({ singleManufacturerPriceType: target.value });
    }

    private _setManufacturerCodeState(selectedMfrCodes: Option[], reportIdentifier: string) {
        const { priceFilesReportText, salesByMfrReportText, salesByPartReportText } = this.props.resources;
        const newSelectedMfrCodeList: string[] = [];
        selectedMfrCodes.map(code => {
            newSelectedMfrCodeList.push(code.value);
        });
        switch (reportIdentifier) {
            case priceFilesReportText:
                this.setState({ priceByMfrSelectedMfrMultiSelect: selectedMfrCodes, priceByMfrSelectedMfrCodes: newSelectedMfrCodeList });
                break;
            case salesByMfrReportText:
                this.setState({ salesByMfrSelectedMfrMultiSelect: selectedMfrCodes, salesByMfrSelectedMfrCodes: newSelectedMfrCodeList });
                break;
            case salesByPartReportText:
                this.setState({
                    salesByPartSelectedPartMultiSelect: selectedMfrCodes,
                    salesByPartSelectedPartCodes: newSelectedMfrCodeList
                });
                break;
            default:
                break;
        }
    }

    private _clearSelectedManufacturers(reportIdentifier: string) {
        const { priceFilesReportText, salesByMfrReportText, salesByPartReportText } = this.props.resources;
        switch (reportIdentifier) {
            case priceFilesReportText:
                const priceSelectElement: HTMLElement | null = document.getElementById('price-files-select');
                if (priceSelectElement) {
                    (priceSelectElement as HTMLSelectElement).selectedIndex = 0;
                }
                this.setState({
                    priceByMfrSelectedMfrMultiSelect: [],
                    priceByMfrSelectedMfrCodes: [],
                    singleManufacturerPriceType: ''
                });
                break;
            case salesByMfrReportText:
                this.setState({ salesByMfrSelectedMfrMultiSelect: [], salesByMfrSelectedMfrCodes: [] });
                break;
            case salesByPartReportText:
                const salesSelectElement: HTMLElement | null = document.getElementById('sales-searchtype-select');
                if (salesSelectElement) {
                    (salesSelectElement as HTMLSelectElement).selectedIndex = 0;
                }
                this.setState({
                    salesByPartSelectedPartMultiSelect: [],
                    salesByPartSelectedPartCodes: [],
                    salesByPartSelectedSearchType: ''
                });
                break;
            default:
                break;
        }
        this._renderRow();
    }

    private _setMultiSelectRendering(selected: Option[], numOfSelectedManufacturers: string, noSelectedManufacturers: string): string {
        let selectedString = numOfSelectedManufacturers;
        selected.length
            ? (selectedString = selectedString.replace('{0}', `${selected.length}`))
            : (selectedString = noSelectedManufacturers);
        return selectedString;
    }

    private _renderSalesDropdown(): React.ReactElement {
        const { organizationDropdownLabel, locationDropdownLabel, pleaseSelectDefaultLabel } = this.props.resources;
        const searchTypeList = [organizationDropdownLabel, locationDropdownLabel];
        return (
            <select
                name='sales-searchtype-select'
                id='sales-searchtype-select'
                className={`sales-searchtype-select__dropdown`}
                onChange={this._setSalesByPartSearchTypeState}
            >
                <option value={pleaseSelectDefaultLabel} key={`${pleaseSelectDefaultLabel}-sales`}>
                    {pleaseSelectDefaultLabel}
                </option>
                {searchTypeList.map(searchType => (
                    <option value={searchType} key={searchType}>
                        {searchType}
                    </option>
                ))}
            </select>
        );
    }

    private _renderManufacturerDropdown(reportIdentifier: string): React.ReactElement {
        const {
            config: { allowMultiSelectForManufacturers, allowSelectAllManufacturers },
            resources: {
                numOfSelectedManufacturers,
                noSelectedManufacturers,
                selectManufacturersLabel,
                numOfSelectedParts,
                noSelectedParts,
                selectPartsLabel,
                salesByMfrReportText,
                priceFilesReportText,
                pleaseSelectDefaultLabel
            }
        } = this.props;

        const needManufacturers = reportIdentifier === salesByMfrReportText || reportIdentifier === priceFilesReportText;
        const options: Option[] = [];
        let noneSelectedLabel = '';
        let numOfSelectedLabel = '';
        let selectLabel = '';

        if (needManufacturers) {
            noneSelectedLabel = noSelectedManufacturers;
            numOfSelectedLabel = numOfSelectedManufacturers;
            selectLabel = selectManufacturersLabel;
            const mfrCodeList = this._getManufacturerCodeList();
            if (mfrCodeList) {
                mfrCodeList.map(code => {
                    if (code.GDNManufacturerCode && code.Description) {
                        const label = `${code.GDNManufacturerCode} - ${code.Description}`;
                        const value = code.GDNManufacturerCode;
                        const optionRow = { label: label, value: value };
                        options.push(optionRow);
                    }
                });
            }
        } else {
            noneSelectedLabel = noSelectedParts;
            numOfSelectedLabel = numOfSelectedParts;
            selectLabel = selectPartsLabel;
            const partList = this._getPartList();
            if (partList) {
                partList.map(part => {
                    if (part.PartNo && part.Description) {
                        const label = `${part.PartNo}`;
                        const value = part.PartNo;
                        const optionRow = { label: label, value: value };
                        options.push(optionRow);
                    }
                });
            }
        }

        return allowMultiSelectForManufacturers ? (
            <MultiSelect
                value={this._getMultiSelectValue(reportIdentifier)}
                onChange={(selectedCodes: Option[]) => this._setManufacturerCodeState(selectedCodes, reportIdentifier)}
                options={options}
                labelledBy={selectLabel}
                disableSearch={true}
                hasSelectAll={allowSelectAllManufacturers ?? false}
                // shouldToggleOnHover={true}
                valueRenderer={selected => this._setMultiSelectRendering(selected, numOfSelectedLabel, noneSelectedLabel)}
            />
        ) : (
            <select
                name='price-files-select'
                id='price-files-select'
                className={`sales-searchtype-select__dropdown`}
                onChange={this._setSingleManufacturerPriceTypeState}
            >
                <option value={pleaseSelectDefaultLabel} key={`${pleaseSelectDefaultLabel}-price`} selected disabled hidden>
                    {pleaseSelectDefaultLabel}
                </option>
                {options.map(mfr => (
                    <option value={mfr.label} key={mfr.label}>
                        {mfr.label}
                    </option>
                ))}
            </select>
        );
    }

    private _getMultiSelectValue(reportIdentifier: string) {
        const { priceFilesReportText, salesByMfrReportText, salesByPartReportText } = this.props.resources;
        switch (reportIdentifier) {
            case priceFilesReportText:
                return this.state.priceByMfrSelectedMfrMultiSelect;
            case salesByMfrReportText:
                return this.state.salesByMfrSelectedMfrMultiSelect;
            case salesByPartReportText:
                return this.state.salesByPartSelectedPartMultiSelect;
            default:
                return [];
        }
    }

    private _toggleModalNotificationActive = () =>
        this.setState(prevState => {
            return {
                isModalActive: !prevState.isModalActive
            };
        });

    private _renderModal = () => {
        const { isModalActive, isModalMessageSuccess, modalMessage } = this.state;
        const { requestFailed, requestSuccessFul } = this.props.resources;
        return (
            <Modal
                isOpen={isModalActive}
                toggle={this._toggleModalNotificationActive}
                className={`eve-reports-export-wrapper__modal eve-reports-export-wrapper__modal${
                    isModalMessageSuccess ? '' : '--error-message'
                }`}
            >
                <ModalHeader>
                    <div className={`eve-reports-export-wrapper__modal-header-wrapper`}>
                        <h5 className='msc-modal__title'>{isModalMessageSuccess ? requestSuccessFul : requestFailed}</h5>
                        <div
                            className={`eve-reports-export-wrapper__modal-header-wrapper--close msc-modal__close-button`}
                            onClick={this._toggleModalNotificationActive}
                        ></div>
                    </div>
                </ModalHeader>
                <ModalBody className={`eve-reports-export-wrapper__modal-body`}>{modalMessage}</ModalBody>
            </Modal>
        );
    };

    private async _handleButtonClick(rowText: string) {
        const {
            config: { allowMultiSelectForManufacturers },
            resources: { priceFilesReportText, salesByMfrReportText, salesByPartReportText }
        } = this.props;
        const { priceByMfrSelectedMfrCodes, salesByPartSelectedSearchType, singleManufacturerPriceType } = this.state;
        if (rowText === priceFilesReportText) {
            const mfrCodeListString: string = allowMultiSelectForManufacturers
                ? priceByMfrSelectedMfrCodes.join(';')
                : singleManufacturerPriceType;
            void getPriceByManufacturerAsync({ callerContext: this.props.context.actionContext }, mfrCodeListString).then(res => {
                this._setModalMessageAndToggle(res.PriceByManufacturerMessage || '', res.DidPriceByManufacturerComplete);
                if (res.DidPriceByManufacturerComplete) {
                    this._clearSelectedManufacturers(rowText);
                }
            });
        } else if (rowText === salesByPartReportText) {
            try {
                const salesReportResult = await getSalesReportAsync(
                    { callerContext: this.props.context.actionContext },
                    salesByPartSelectedSearchType
                );
                this._setModalMessageAndToggle(salesReportResult.SalesReportMessage || '', salesReportResult.DidSalesReportComplete);
                this.setState({ salesByPartSelectedSearchType: '' });
                this._clearSelectedManufacturers(rowText);
                // void getSalesReportAsync({ callerContext: this.props.context.actionContext }, salesByPartSelectedSearchType).then(res => {
                //     this.setState({
                //         modalMessage: res.SalesReportMessage || '',
                //         isModalMessageSuccess: res.DidSalesReportComplete
                //     });
                //     this._toggleModalNotificationActive();
                //     this.setState({ salesByPartSelectedSearchType: '' });
                // });
            } catch (ex) {
                this._setModalMessageAndToggle((ex as Error).message, false);
                this.setState({ salesByPartSelectedSearchType: '' });
            }
        } else if (rowText === salesByMfrReportText) {
            // api call
            this._clearSelectedManufacturers(rowText);
            console.log('Request Sales by Manufacturer Report.');
        } else {
            console.log('Error');
        }
    }

    private _setModalMessageAndToggle(message: string, messageSuccess: boolean) {
        const { requestSuccessfulMessageText, requestFailedMessageText } = this.props.resources;
        this.setState({
            modalMessage: `${messageSuccess ? requestSuccessfulMessageText : requestFailedMessageText} ${message}`,
            isModalMessageSuccess: messageSuccess
        });
        this._toggleModalNotificationActive();
    }

    private _renderRow() {
        const {
            config: { allowMultiSelectForManufacturers },
            resources: { priceFilesReportText, salesByPartReportText, requestReportsBtnText, pleaseSelectDefaultLabel }
        } = this.props;
        const { salesByPartSelectedSearchType, singleManufacturerPriceType } = this.state;

        const rowTextList = [priceFilesReportText, salesByPartReportText];
        return (
            <div>
                <ul className={'eve-reports-export-wrapper-table'}>
                    {rowTextList.map((rowText, i) => (
                        <li key={i} className={'eve-reports-export-wrapper-table-row'}>
                            {rowText === salesByPartReportText ? (
                                <RenderCard
                                    rowText={rowText}
                                    buttonText={requestReportsBtnText}
                                    className={`eve-reports-export-wrapper-table-row`}
                                    onClick={this._handleButtonClick}
                                    isButtonDisabled={
                                        salesByPartSelectedSearchType !== '' && salesByPartSelectedSearchType !== pleaseSelectDefaultLabel
                                            ? false
                                            : true
                                    }
                                    dropdown={this._renderSalesDropdown()}
                                />
                            ) : rowText === priceFilesReportText && !allowMultiSelectForManufacturers ? (
                                <RenderCard
                                    rowText={rowText}
                                    buttonText={requestReportsBtnText}
                                    className={`eve-reports-export-wrapper-table-row`}
                                    onClick={this._handleButtonClick}
                                    isButtonDisabled={
                                        singleManufacturerPriceType !== '' && singleManufacturerPriceType !== pleaseSelectDefaultLabel
                                            ? false
                                            : true
                                    }
                                    dropdown={this._renderManufacturerDropdown(rowText)}
                                />
                            ) : (
                                <RenderCard
                                    rowText={rowText}
                                    buttonText={requestReportsBtnText}
                                    className={`eve-reports-export-wrapper-table-row`}
                                    onClick={this._handleButtonClick}
                                    isButtonDisabled={this._getMultiSelectValue(rowText).length > 0 ? false : true}
                                    dropdown={this._renderManufacturerDropdown(rowText)}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => {
        this.props.config.heading.text = event.target.value;
    };

    public render(): JSX.Element | null {
        const _className = `eve-reports-export-wrapper`;
        const {
            config: { className = '', heading }
        } = this.props;
        const viewProps = {
            ...this.props,
            moduleProps: {
                moduleProps: this.props,
                className: classnames(_className, className)
            },
            className,
            AccountOrderTemplatesTile: {
                moduleProps: this.props,
                className: classnames(`${_className}__heading`, className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className={`${_className}__heading`}
                    tag={heading.tag}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            reportsList: this._renderRow(),
            modal: this._renderModal()
        };
        return this.props.renderView(viewProps);
    }
}

export default ReportsExport;
